import ReactMarkdown from "react-markdown";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PRIVACY_POLICY } from "@/content/legal/privacy";
import { TERMS_OF_SERVICE } from "@/content/legal/terms";

// Keyed by the consent id served from /public/consents, so a consent row only
// grows a "Read" link when there is a document behind it — `marketing` and
// `broker_sharing` deliberately have none.
export const LEGAL_DOCS = {
    terms: TERMS_OF_SERVICE,
    privacy: PRIVACY_POLICY,
};

// Module scope so react-markdown doesn't remount the tree on every render
// (react/no-unstable-nested-components), same pattern as DealDetail.
const MARKDOWN_COMPONENTS = {
    h1: () => null, // the doc title is already the dialog title
    h2: ({ node, ...props }) => (
        <h2
            {...props}
            className="text-sm font-semibold text-navy-800 mt-6 mb-2 first:mt-0"
        />
    ),
    h3: ({ node, ...props }) => (
        <h3 {...props} className="text-sm font-medium text-navy-700 mt-4 mb-1.5" />
    ),
    p: ({ node, ...props }) => <p {...props} className="mb-3" />,
    ul: ({ node, ...props }) => (
        <ul {...props} className="list-disc pl-5 mb-3 space-y-1" />
    ),
    ol: ({ node, ...props }) => (
        <ol {...props} className="list-decimal pl-5 mb-3 space-y-1" />
    ),
    strong: ({ node, ...props }) => (
        <strong {...props} className="font-semibold text-navy-800" />
    ),
    a: ({ node, ...props }) => (
        <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gold-600 underline underline-offset-2 hover:text-gold-700"
        />
    ),
};

const ALLOWED_ELEMENTS = [
    "h1", "h2", "h3", "p", "a", "strong", "em", "ul", "ol", "li", "br",
];

/**
 * Read-only viewer for a Perx legal document.
 *
 * Deliberately a modal rather than a route: it is opened from the consent
 * checkboxes on the signup form, and navigating away mid-signup would throw
 * away everything the member has typed. The body is bundled markdown (see
 * src/content/legal/) so it renders offline, needs no API round trip, and
 * stays readable on mobile in a way an embedded PDF does not.
 */
export default function LegalDocDialog({ doc, open, onOpenChange, hint }) {
    if (!doc) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid={`legal-dialog-${doc.version}`}
            >
                <DialogHeader>
                    <DialogTitle className="text-navy-900">{doc.title}</DialogTitle>
                    {hint && <DialogDescription>{hint}</DialogDescription>}
                </DialogHeader>
                <ScrollArea className="max-h-[60vh] pr-4 -mr-2">
                    <div className="text-sm text-navy-600 leading-relaxed">
                        <ReactMarkdown
                            components={MARKDOWN_COMPONENTS}
                            allowedElements={ALLOWED_ELEMENTS}
                            unwrapDisallowed
                        >
                            {doc.body}
                        </ReactMarkdown>
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
}
